import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaGoogle, FaApple } from "react-icons/fa";
import { auth, db } from "./firebase"; // <-- ensure db is exported
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import heroImg from "./img/hero-img.jpg";

export default function Auth({ onSignIn }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setInfoMsg("");
    setLoading(true);

    const form = e.currentTarget;
    const email = form.elements.email.value.trim();
    const password = form.elements.password.value;

    let phone = "";
    let fullName = "";

    if (isSignUp) {
      const confirmPassword = form.elements.confirmPassword?.value || "";
      if (password !== confirmPassword) {
        setErrorMsg("Passwords do not match");
        setLoading(false);
        return;
      }
      fullName = (form.elements.fullname?.value || "").trim() + " " + (form.elements.lastname?.value || "").trim();
      fullName = fullName.trim();

      phone = (form.elements.phone?.value || "").trim();
      // Basic sanity check; adjust as you like or swap for a phone input lib.
      const phoneOk = /^\+?[0-9\s-]{8,20}$/.test(phone);
      if (!phoneOk) {
        setErrorMsg("Please enter a valid phone number (e.g., +91 9876543210).");
        setLoading(false);
        return;
      }
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || "/dashboard";

      if (isSignUp) {
        // Create auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Update display name (Firebase Auth does NOT store phone for email/password accounts)
        if (fullName) {
          await updateProfile(user, { displayName: fullName });
        }

        // Store phone (and other profile bits) in Firestore
        try {
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            email,
            displayName: fullName || null,
            phone,                           // <-- stored here
            provider: "password",
            createdAt: serverTimestamp(),
          }, { merge: true });
        } catch (e) {
          console.warn("Firestore write failed, continuing:", e);
        }

        onSignIn?.(user);
        navigate(next);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        onSignIn?.(userCredential.user);
        navigate(next);
      }
    } catch (err) {
      switch (err.code) {
        case "auth/email-already-in-use":
          setErrorMsg("An account with this email already exists.");
          break;
        case "auth/weak-password":
          setErrorMsg("Password should be at least 6 characters long.");
          break;
        case "auth/invalid-email":
          setErrorMsg("Please enter a valid email address.");
          break;
        case "auth/user-not-found":
          setErrorMsg("No account found. Please sign up first.");
          break;
        case "auth/wrong-password":
          setErrorMsg("Incorrect password. Try again.");
          break;
        default:
          setErrorMsg(err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setInfoMsg("");
    const form = e.currentTarget.form || e.currentTarget.closest("form");
    const email = form?.elements?.email?.value || "";
    if (!email) {
      setErrorMsg("Please enter your email above to reset your password.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setInfoMsg("Password reset email sent. Please check your inbox.");
    } catch (err) {
      switch (err.code) {
        case "auth/invalid-email":
          setErrorMsg("Please enter a valid email address.");
          break;
        case "auth/user-not-found":
          setErrorMsg("No account found with this email.");
          break;
        default:
          setErrorMsg(err.message);
      }
    }
  };

  let googleSignInPromise = null;
  const handleGoogleSignIn = async () => {
    if (loading || googleSignInPromise) return;
    setLoading(true);
    setErrorMsg("");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || "/dashboard";
      googleSignInPromise = signInWithPopup(auth, provider);
      const cred = await googleSignInPromise;
      googleSignInPromise = null;

      // Optional: ensure user doc exists (no phone here yet)
      try {
        await setDoc(doc(db, "users", cred.user.uid), {
          uid: cred.user.uid,
          email: cred.user.email || null,
          displayName: cred.user.displayName || null,
          provider: "google",
          createdAt: serverTimestamp(),
        }, { merge: true });
      } catch (e) {
        console.warn("Firestore write (Google) failed:", e);
      }

      onSignIn?.(cred.user);
      navigate(next);
    } catch (err) {
      googleSignInPromise = null;
      setErrorMsg("Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#1A1A1D] text-white overflow-hidden">
      {/* Left side */}
      <div className="hidden md:flex flex-col justify-between bg-[#2B0B4B] relative w-1/2 rounded-r-3xl overflow-hidden">
        <img src={heroImg} alt="Hero" className="w-full h-full object-cover opacity-90" />
        <button
          onClick={() => navigate("/")}
          className="absolute top-6 right-6 bg-white/20 backdrop-blur-md text-white text-sm px-4 py-2 rounded-full hover:bg:white/30 transition-all"
        >
          ← Back to website
        </button>
      </div>

      {/* Right side (Form) */}
      <div className="flex items-center justify-center flex-1 bg-[#1E1B2A] px-6 md:px-16 py-12">
        <div className="w-full max-w-md bg-[#221F35] p-8 rounded-2xl shadow-xl">
          <h1 className="text-3xl font-bold mb-2 text-white">
            {isSignUp ? "Create an account" : "Welcome back"}
          </h1>
          <p className="text-sm text-gray-400 mb-8">
            {isSignUp ? (
              <>
                Already have an account?{" "}
                <button type="button" onClick={() => setIsSignUp(false)} className="text-indigo-400 hover:underline">
                  Log in
                </button>
              </>
            ) : (
              <>
                Don’t have an account?{" "}
                <button type="button" onClick={() => setIsSignUp(true)} className="text-indigo-400 hover:underline">
                  Create one
                </button>
              </>
            )}
          </p>

          {errorMsg && <p className="text-red-400 text-sm mb-4">{errorMsg}</p>}
          {infoMsg && <p className="text-green-400 text-sm mb-4">{infoMsg}</p>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div className="flex gap-3">
                <input
                  type="text"
                  name="fullname"
                  placeholder="First name"
                  className="w-1/2 px-4 py-3 rounded-lg bg-[#2C2743] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  required
                />
                <input
                  type="text"
                  name="lastname"
                  placeholder="Last name"
                  className="w-1/2 px-4 py-3 rounded-lg bg-[#2C2743] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            )}

            <input
              type="email"
              name="email"
              placeholder="Email"
              className="w-full px-4 py-3 rounded-lg bg-[#2C2743] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            />

            {/* Phone for Sign Up */}
            {isSignUp && (
              <input
                type="tel"
                name="phone"
                placeholder="Phone number (e.g. +91 9876543210)"
                className="w-full px-4 py-3 rounded-lg bg-[#2C2743] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                inputMode="tel"
                autoComplete="tel"
                required
              />
            )}

            <input
              type="password"
              name="password"
              placeholder="Enter your password"
              className="w-full px-4 py-3 rounded-lg bg-[#2C2743] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            />

            {!isSignUp && (
              <div className="text-right -mt-2">
                <button type="button" onClick={handleForgotPassword} className="text-indigo-400 text-sm hover:underline">
                  Forgot password?
                </button>
              </div>
            )}

            {isSignUp && (
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm your password"
                className="w-full px-4 py-3 rounded-lg bg-[#2C2743] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                required
              />
            )}

            {isSignUp && (
              <label className="flex items-center gap-2 text-sm text-gray-400">
                <input type="checkbox" required className="accent-indigo-400" />
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">
                  Terms & Conditions
                </a>
              </label>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-indigo-500 rounded-lg font-semibold hover:bg-indigo-600 transition-all disabled:opacity-60"
            >
              {loading ? "Processing..." : isSignUp ? "Create account" : "Sign in with Email"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-gray-600 flex-1" />
            <span className="text-gray-400 text-sm">
              {isSignUp ? "or register with" : "Continue with"}
            </span>
            <div className="h-px bg-gray-600 flex-1" />
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleGoogleSignIn}
              className="flex-1 py-3 bg-[#2C2743] border border-gray-600 rounded-lg flex items-center justify-center gap-2 hover:bg-[#3C3361] transition-all"
            >
              <FaGoogle className="text-lg" /> Google
            </button>
            <button
              type="button"
              className="flex-1 py-3 bg-[#2C2743] border border-gray-600 rounded-lg flex items-center justify-center gap-2 hover:bg-[#3C3361] transition-all"
            >
              <FaApple className="text-xl" /> Apple
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
